import { useMemo } from 'react';
import { computeRaceFrame, TOP_N, type RaceFrame } from '../../utils/barRace';
import { Logo } from '../common/Logo';
import type { RankingTimelineData } from '../../types';

// Instagram-Stories safe area (CSS px on the 540×960 capture): keep content clear
// of the top progress bar / profile row and the bottom reply/reactions bar.
const STORIES_PAD_TOP = 56;
const STORIES_PAD_BOTTOM = 92;

/**
 * Shared, purely-presentational bar-chart-race stage. Given the timeline and a
 * time `t` (ms), it renders the exact interpolated frame. Rows are absolutely
 * positioned and updated directly every frame (no CSS transitions — the rAF
 * clock, or the Puppeteer seek, provides the smoothness), so what plays in the
 * app is frame-identical to the captured video.
 *
 * Used by both `RankingEvolutionView` (in-app) and the `/capture/ranking-evolution`
 * page (server-side render).
 */

export interface BarRaceStageProps {
  timeline: RankingTimelineData;
  tMs: number;
  rankingName: string;
  rankingImageUrl?: string | null;
  /** Row pitch in px. Larger for the 1080×1920 capture. */
  rowHeight?: number;
  /** Show the ranking image + name intro overlay + outro watermark (capture). */
  showChrome?: boolean;
}

const LEADER_GRADIENT = 'linear-gradient(90deg, #F5B841 0%, #F5D76E 100%)'; // sunny
const BAR_GRADIENT = 'linear-gradient(90deg, #17B7A6 0%, #3DD6C4 100%)'; // turquoise

function initial(name: string): string {
  return name?.trim()?.charAt(0)?.toUpperCase() || '?';
}

export function BarRaceStage({
  timeline,
  tMs,
  rankingName,
  rankingImageUrl,
  rowHeight = 52,
  showChrome = false,
}: BarRaceStageProps) {
  const playerById = useMemo(
    () => new Map(timeline.players.map((p) => [p.id, p])),
    [timeline.players],
  );

  const frame: RaceFrame = useMemo(
    () => computeRaceFrame(timeline, tMs),
    [timeline, tMs],
  );

  // Reserve only as many rows as there are players (up to TOP_N) so small
  // rankings don't leave a big empty gap below the last bar.
  const visibleRows = Math.max(1, Math.min(TOP_N, timeline.players.length));
  const barsHeight = visibleRows * rowHeight;
  const avatar = Math.round(rowHeight * 0.62);
  const introVisible = showChrome && frame.phase === 'intro';

  return (
    <div
      className="relative w-full h-full flex flex-col overflow-hidden text-white select-none"
      style={
        showChrome
          ? { paddingTop: STORIES_PAD_TOP, paddingBottom: STORIES_PAD_BOTTOM }
          : undefined
      }
    >
      {/* Header: ranking identity + round counter */}
      <div className="shrink-0 px-5 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg overflow-hidden ring-1 ring-white/15 bg-gradient-to-br from-turquoise/40 to-coral/40 flex items-center justify-center text-white font-bold text-sm shrink-0">
            {rankingImageUrl ? (
              <img src={rankingImageUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              rankingName.trim().charAt(0).toUpperCase() || '?'
            )}
          </div>
          <span className="text-sm font-semibold text-white/70 truncate">
            Ranking {rankingName}
          </span>
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-2xl font-extrabold tracking-tight text-white">
            {frame.roundLabel || 'Evolução da temporada'}
          </span>
        </div>
        <p className="text-xs text-white/40 mt-0.5">Evolução da temporada</p>
      </div>

      {/* Bars */}
      <div className="flex-1 min-h-0 px-4 overflow-hidden">
        <div className="relative w-full" style={{ height: barsHeight }}>
          {frame.rows.map((row) => {
            const player = playerById.get(row.playerId);
            const isLeader = row.position === 1;
            return (
              <div
                key={row.playerId}
                className="absolute left-0 right-0 flex items-center gap-2"
                style={{
                  top: 0,
                  height: rowHeight,
                  transform: `translateY(${row.y * rowHeight}px)`,
                  opacity: row.opacity,
                  willChange: 'transform, opacity',
                }}
              >
                <div
                  className="shrink-0 text-center font-bold text-white/50"
                  style={{ width: 22, fontSize: Math.round(rowHeight * 0.26) }}
                >
                  {row.rank}
                </div>

                <div
                  className="relative flex-1 rounded-lg overflow-hidden bg-white/[0.06]"
                  style={{ height: rowHeight - 12 }}
                >
                  {/* Fill */}
                  <div
                    className="absolute inset-y-0 left-0 rounded-lg"
                    style={{
                      width: `${row.widthPct}%`,
                      background: isLeader ? LEADER_GRADIENT : BAR_GRADIENT,
                    }}
                  />
                  {/* Content overlaid across the full track so it stays legible */}
                  <div className="absolute inset-0 flex items-center gap-2 pl-1.5 pr-3">
                    <div
                      className="shrink-0 rounded-full bg-deep-blue/40 ring-1 ring-white/20 flex items-center justify-center overflow-hidden font-bold text-white"
                      style={{ width: avatar, height: avatar, fontSize: Math.round(avatar * 0.42) }}
                    >
                      {player?.avatarUrl ? (
                        <img
                          src={player.avatarUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        initial(player?.name || '')
                      )}
                    </div>
                    <span
                      className="font-semibold truncate text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.55)]"
                      style={{ fontSize: Math.round(rowHeight * 0.28) }}
                    >
                      {player?.name || 'Jogador'}
                    </span>
                    <span
                      className="ml-auto font-extrabold tabular-nums text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]"
                      style={{ fontSize: Math.round(rowHeight * 0.3) }}
                    >
                      {row.displayPoints}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Brand mark (capture) */}
      {showChrome && (
        <div className="shrink-0 px-5 pt-3 flex justify-center">
          <Logo size="md" />
        </div>
      )}

      {/* Intro overlay (capture): ranking image + name. Respects Stories padding
          via `inset-0` on the padded root. */}
      {introVisible && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-deep-blue">
          <div className="w-32 h-32 rounded-2xl overflow-hidden ring-2 ring-white/15 shadow-xl bg-gradient-to-br from-turquoise/40 to-coral/40 flex items-center justify-center text-white font-extrabold text-5xl">
            {rankingImageUrl ? (
              <img src={rankingImageUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              rankingName.trim().charAt(0).toUpperCase() || '?'
            )}
          </div>
          <div className="text-center px-8">
            <p className="text-sm font-semibold text-white/50 uppercase tracking-wide">Ranking</p>
            <h1 className="text-3xl font-extrabold text-white">{rankingName}</h1>
            <p className="mt-2 text-base text-sunny font-semibold">Evolução da temporada</p>
          </div>
          <div className="absolute left-0 right-0 flex justify-center" style={{ bottom: 8 }}>
            <Logo size="md" />
          </div>
        </div>
      )}
    </div>
  );
}
